import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LevelBadge,
  Loading,
  PageHeader,
  Select,
  Toggle,
} from '@/components/ui';
import { formatDateTime, formatNumber } from '@/lib/format';

/**
 * The platform log, as something that can be searched.
 *
 * It used to be a paginated dump: a page number, a level, and scrolling. The
 * stuck-call defect that took five days was eventually found by ignoring this
 * page entirely and grepping journalctl on the box, which is a fair verdict on
 * what it was.
 *
 * Four things narrow it now — a phrase, a time window, which part of the
 * platform it came from, and which call it was about. The last is the one that
 * matters: the call id has been in the context column of every line for
 * releases, and there was no way to ask for it.
 */

interface Row {
  id: number;
  level: string;
  source: string;
  message: string;
  context: Record<string, unknown> | string | null;
  tenant_id: string | null;
  client_name: string | null;
  created_at: string;
}

interface Response {
  data: Row[];
  page: number;
  page_size: number;
  total: number;
  window_hours: number;
  levels: Array<{ level: string; count: number }>;
  sources: Array<{ source: string; count: number }>;
}

const WINDOWS = [
  { hours: 1, label: 'Last hour' },
  { hours: 6, label: 'Last 6 hours' },
  { hours: 24, label: 'Last 24 hours' },
  { hours: 72, label: 'Last 3 days' },
  { hours: 168, label: 'Last 7 days' },
];

function asObject(context: Row['context']): Record<string, unknown> {
  if (!context) return {};
  if (typeof context === 'string') {
    try {
      return JSON.parse(context);
    } catch {
      return { context };
    }
  }
  return context;
}

export default function SystemLogs() {
  const [params, setParams] = useSearchParams();

  const [q, setQ] = useState(params.get('q') ?? '');
  const [level, setLevel] = useState(params.get('level') ?? '');
  const [source, setSource] = useState(params.get('source') ?? '');
  const [callId, setCallId] = useState(params.get('call_id') ?? '');
  const [hours, setHours] = useState(Number(params.get('hours') ?? 24));
  const [page, setPage] = useState(1);
  const [follow, setFollow] = useState(false);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [purging, setPurging] = useState<null | number>(null);

  const queryClient = useQueryClient();

  /**
   * Download the current view as a text file.
   *
   * Straight to the browser rather than through the JSON client: this is a
   * file, it can be twenty thousand lines, and holding it in memory to hand it
   * back as a string helps nobody.
   */
  async function exportLogs() {
    // Fetched through the authenticated client, not `window.open`.
    //
    // A plain window.open carries no Authorization header, so the export would
    // have answered 401 every time -- a download button that silently does
    // nothing, which is worse than no button.
    const blob = await api.getBlob('/admin/system-logs/export', {
      hours,
      q: search || undefined,
      level: level || undefined,
      source: source || undefined,
      call_id: callId || undefined,
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = callId
      ? `glee-log-${callId.slice(0, 8)}.txt`
      : `glee-log-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.txt`;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Delete log lines.
   *
   * The sink already drops lines past a retention window, which stops the
   * table growing forever and is the wrong tool for the two occasions somebody
   * actually wants this: a burst of noise from a fault that has since been
   * fixed and is now drowning the viewer, and an install being handed over
   * with a test campaign's worth of lines in it.
   *
   * Trimming is offered first and is the one to reach for. Emptying it is not
   * recoverable — these lines exist in one place.
   */
  const purge = useMutation({
    mutationFn: (days: number) =>
      // `api.delete` takes a path only, so the window goes in the query
      // string. Zero means everything, and is deliberately not sent as
      // `older_than_days=0` -- the server would read that as falsy and empty
      // the table, which is the right answer for the wrong reason.
      api.delete<{ deleted: number }>(
        days > 0
          ? `/admin/system-logs?older_than_days=${days}`
          : '/admin/system-logs',
      ),
    onSuccess: () => {
      setPurging(null);
      queryClient.invalidateQueries({ queryKey: ['system-logs'] });
    },
  });

  /**
   * The search box is debounced, not the filters.
   *
   * A dropdown is one decision and should take effect at once. A text field is
   * a request per keystroke against a table that can hold weeks of rows, and
   * on a four-core box that is the page making the platform slower while
   * somebody is trying to find out why the platform is slow.
   */
  const [search, setSearch] = useState(q);
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [q]);

  // Kept in the URL so a line can be sent to somebody. "Look at the logs" is
  // not a handover; a link that opens on the same twelve rows is.
  useEffect(() => {
    const next = new URLSearchParams();
    if (search) next.set('q', search);
    if (level) next.set('level', level);
    if (source) next.set('source', source);
    if (callId) next.set('call_id', callId);
    if (hours !== 24) next.set('hours', String(hours));
    setParams(next, { replace: true });
  }, [search, level, source, callId, hours, setParams]);

  const logs = useQuery({
    queryKey: ['system-logs', search, level, source, callId, hours, page],
    queryFn: () =>
      api.get<Response>('/admin/system-logs', {
        page,
        page_size: 200,
        q: search || undefined,
        level: level || undefined,
        source: source || undefined,
        call_id: callId || undefined,
        hours,
      }),
    refetchInterval: follow ? 5_000 : false,
    placeholderData: (previous) => previous,
  });

  const pages = logs.data ? Math.max(1, Math.ceil(logs.data.total / logs.data.page_size)) : 1;
  const filtered = !!(search || level || source || callId);

  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (follow && listRef.current) listRef.current.scrollTop = 0;
  }, [follow, logs.dataUpdatedAt]);


  return (
    <div className="w-full">
      <PageHeader
        title="System logs"
        description="Everything the platform wrote down, and a way to find one line of it."
        actions={
          <div className="flex items-center gap-3">
            <Toggle label="Follow" checked={follow} onChange={setFollow} />
            {/* A button, not a dropdown. The dropdown was there and was
                reported as missing, which is a fair verdict: a destructive
                action hidden inside a select looks like a filter. */}
            {/* Exports exactly what is on screen, filters and all. Getting
                a log off this box has meant SSH, journalctl, a grep and an
                attachment -- every time, for everybody. */}
            <Button variant="secondary" onClick={exportLogs}>
              Export
            </Button>
            <Button variant="secondary" onClick={() => setPurging(1)}>
              Delete old logs
            </Button>
            <Button variant="danger" onClick={() => setPurging(0)}>
              Delete all
            </Button>
            <Select
              value={hours}
              className="w-auto"
              onChange={(e) => {
                setHours(Number(e.target.value));
                setPage(1);
              }}
            >
              {WINDOWS.map((w) => (
                <option key={w.hours} value={w.hours}>
                  {w.label}
                </option>
              ))}
            </Select>
          </div>
        }
      />

      <ConfirmDialog
        open={purging !== null}
        title={purging === 0 ? 'Delete every log line?' : 'Delete older log lines?'}
        confirmLabel={purging === 0 ? 'Delete everything' : 'Delete them'}
        requireTyping={purging === 0 ? 'DELETE' : undefined}
        busy={purge.isPending}
        onCancel={() => setPurging(null)}
        onConfirm={() => purge.mutate(purging ?? 0)}
        body={
          purging === 0 ? (
            <>
              <p>
                This removes <strong>every line</strong> in the platform log,
                for every client. It cannot be undone — these lines exist in one
                place and are not backed up anywhere else.
              </p>
              <p>
                If a burst of noise is what you are trying to clear, delete
                older than one day instead. It keeps whatever is still being
                read.
              </p>
            </>
          ) : (
            <>
              <p>
                This removes every line older than{' '}
                <strong>
                  {purging} day{purging === 1 ? '' : 's'}
                </strong>
                . Anything newer is kept. It cannot be undone.
              </p>
              <Field label="Keep the last">
                <Select
                  value={String(purging)}
                  onChange={(e) => setPurging(Number(e.target.value))}
                >
                  <option value="1">1 day</option>
                  <option value="7">7 days</option>
                  <option value="30">30 days</option>
                  <option value="90">90 days</option>
                </Select>
              </Field>
            </>
          )
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Search" hint="Message or context.">
          <Input
            value={q}
            placeholder="hangup, timeout, ARI…"
            onChange={(e) => setQ(e.target.value)}
          />
        </Field>
        <Field label="Level">
          <Select
            value={level}
            onChange={(e) => {
              setLevel(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Any level</option>
            {(logs.data?.levels ?? []).map((l) => (
              <option key={l.level} value={l.level}>
                {l.level} ({l.count})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Source">
          <Select
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setPage(1);
            }}
          >
            <option value="">Anywhere</option>
            {(logs.data?.sources ?? []).map((s) => (
              <option key={s.source} value={s.source}>
                {s.source} ({s.count})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Call id" hint="Every line a single call wrote.">
          <Input
            value={callId}
            placeholder="cfc5a33c-…"
            className="font-mono text-xs"
            onChange={(e) => {
              setCallId(e.target.value.trim());
              setPage(1);
            }}
          />
        </Field>
      </div>

      {/* One row of counts, which is also the fastest way to narrow: a reader
          who wants the errors clicks the errors. */}
      {!!logs.data?.levels.length && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-ink-500">
            {formatNumber(logs.data.total)} line{logs.data.total === 1 ? '' : 's'}
          </span>
          {logs.data.levels.map((l) => (
            <button
              key={l.level}
              onClick={() => {
                setLevel(level === l.level ? '' : l.level);
                setPage(1);
              }}
              className={
                'rounded-md px-1.5 py-0.5 ring-1 ring-inset transition-colors ' +
                (level === l.level
                  ? 'bg-brand-600/10 text-brand-700 ring-brand-600/30 dark:text-brand-300'
                  : 'text-ink-500 ring-line hover:text-ink-800 dark:hover:text-ink-200')
              }
            >
              {l.level} {formatNumber(l.count)}
            </button>
          ))}
          {filtered && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setQ('');
                setLevel('');
                setSource('');
                setCallId('');
                setPage(1);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      )}

      <Card className="mt-3" padded={false}>
        {logs.isPending ? (
          <Loading />
        ) : logs.error ? (
          <ErrorState error={logs.error} retry={() => logs.refetch()} />
        ) : !logs.data.data.length ? (
          <EmptyState
            title="Nothing matched"
            description={
              filtered
                ? 'Widen the window or clear a filter.'
                : 'Nothing has been logged in this window.'
            }
          />
        ) : (
          <div ref={listRef} className="max-h-[70vh] overflow-y-auto">
            <ul className="divide-y divide-line font-mono text-xs">
              {logs.data.data.map((row) => {
                const context = asObject(row.context);
                const rowCallId = String(context.call_id ?? '');
                const open = expanded === row.id;
                return (
                  <li
                    key={row.id}
                    className="px-3 py-1.5 hover:bg-ink-50/60 dark:hover:bg-ink-900/40"
                  >
                    <div className="flex items-start gap-2">
                      <span className="w-[8.5rem] shrink-0 tabular-nums text-ink-400">
                        {formatDateTime(row.created_at)}
                      </span>
                      <span className="w-16 shrink-0">
                        <LevelBadge level={row.level} />
                      </span>
                      <button
                        onClick={() => setSource(source === row.source ? '' : row.source)}
                        className="w-40 shrink-0 truncate text-left text-ink-500 hover:text-brand-600 dark:hover:text-brand-400"
                        title={`Only ${row.source}`}
                      >
                        {row.source}
                      </button>
                      <button
                        onClick={() => setExpanded(open ? null : row.id)}
                        className="min-w-0 flex-1 text-left font-sans text-ink-800 dark:text-ink-100"
                      >
                        <span className={open ? '' : 'line-clamp-1'}>{row.message}</span>
                      </button>
                      {rowCallId && (
                        <button
                          onClick={() => setCallId(rowCallId)}
                          className="shrink-0 text-ink-400 hover:text-brand-600 dark:hover:text-brand-400"
                          title="Every line this call wrote"
                        >
                          call
                        </button>
                      )}
                      {row.client_name && (
                        <Badge tone="gray" className="shrink-0 font-sans">
                          {row.client_name}
                        </Badge>
                      )}
                    </div>

                    {open && (
                      <div className="mt-1.5 ml-[8.5rem] space-y-2 rounded-md bg-sunken p-2.5">
                        <dl className="grid gap-x-4 gap-y-1 sm:grid-cols-2">
                          {Object.entries(context).map(([key, value]) => (
                            <div key={key} className="flex gap-2">
                              <dt className="shrink-0 text-ink-400">{key}</dt>
                              <dd className="min-w-0 break-all text-ink-700 dark:text-ink-200">
                                {typeof value === 'object'
                                  ? JSON.stringify(value)
                                  : String(value)}
                              </dd>
                            </div>
                          ))}
                        </dl>
                        {rowCallId && (
                          <Link
                            to={`/calls/${rowCallId}`}
                            className="inline-block font-sans text-brand-600 hover:underline dark:text-brand-400"
                          >
                            Open this call →
                          </Link>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Card>

      {logs.data && pages > 1 && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-ink-500">
            Page {page} of {formatNumber(pages)}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Newer
            </Button>
            <Button
              variant="secondary"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Older
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
